import { BarChart3, TrendingUp, Users, Music, DollarSign, UserPlus, Gift, ShoppingCart, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useStats, useGenerations, useUsers, useTransactions, useTopReferrers, useReferralStats, useFullTrackStats } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ['hsl(var(--primary))', 'hsl(var(--pink))', 'hsl(var(--electric))', '#10b981', '#f59e0b'];

export default function Stats() {
  const { data: stats, isLoading: statsLoading } = useStats();
  const { data: generations } = useGenerations();
  const { data: users } = useUsers();
  const { data: transactions } = useTransactions();
  const { data: topReferrers, isLoading: referrersLoading } = useTopReferrers(10);
  const { data: referralStats } = useReferralStats();
  const { data: fullTrackStats, isLoading: fullTrackLoading } = useFullTrackStats();

  // Status distribution
  const statusCounts = generations?.reduce((acc, gen) => {
    acc[gen.status] = (acc[gen.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  // Mock daily data (in real app, this would come from aggregated queries)
  const dailyData = [
    { day: 'Пн', generations: 45, revenue: 12500 },
    { day: 'Вт', generations: 52, revenue: 15800 },
    { day: 'Ср', generations: 38, revenue: 9200 },
    { day: 'Чт', generations: 61, revenue: 18400 },
    { day: 'Пт', generations: 73, revenue: 22100 },
    { day: 'Сб', generations: 89, revenue: 28500 },
    { day: 'Вс', generations: 67, revenue: 19800 },
  ];

  const statCards = [
    { label: "Всего хитов", value: stats?.totalHits || 0, icon: Music, color: "text-primary" },
    { label: "Пользователей", value: stats?.activeUsers || 0, icon: Users, color: "text-pink" },
    { label: "Доход (₽)", value: stats?.monthlyRevenue || 0, icon: DollarSign, color: "text-electric" },
    { label: "Конверсия", value: `${stats?.conversionRate || 0}%`, icon: TrendingUp, color: "text-primary" },
  ];

  return (
    <AdminLayout title="Статистика" description="Аналитика и графики">
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.label} className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardDescription>{stat.label}</CardDescription>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <span className="text-2xl font-bold">
                  {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                </span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Генерации за неделю
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="generations" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status pie chart */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Статусы генераций
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Нет данных
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {statusData.map((item, index) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Track Sales Section */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-green-500" />
          Продажи полных версий
        </h2>
        
        {/* Sales Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-card border-border border-green-500/30">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Zap className="w-3 h-3" /> Сегодня
              </CardDescription>
            </CardHeader>
            <CardContent>
              {fullTrackLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div>
                  <span className="text-2xl font-bold text-green-500">{fullTrackStats?.todayPurchases || 0}</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    ({(fullTrackStats?.todayRevenue || 0).toLocaleString()}₽)
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription>Всего демо</CardDescription>
            </CardHeader>
            <CardContent>
              {fullTrackLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <span className="text-2xl font-bold">{fullTrackStats?.totalDemos || 0}</span>
              )}
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription>Куплено полных</CardDescription>
            </CardHeader>
            <CardContent>
              {fullTrackLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <span className="text-2xl font-bold text-green-500">{fullTrackStats?.totalPurchases || 0}</span>
              )}
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription>Конверсия демо→покупка</CardDescription>
            </CardHeader>
            <CardContent>
              {fullTrackLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <span className="text-2xl font-bold">{fullTrackStats?.conversionRate || 0}%</span>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Revenue Card */}
        <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Доход от полных версий
            </CardTitle>
            <CardDescription>Общая сумма продаж</CardDescription>
          </CardHeader>
          <CardContent>
            {fullTrackLoading ? (
              <Skeleton className="h-12 w-40" />
            ) : (
              <span className="text-4xl font-bold text-green-500">
                {(fullTrackStats?.totalRevenue || 0).toLocaleString()}₽
              </span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Referral Stats Section */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary" />
          Реферальная программа
        </h2>
        
        {/* Referral Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription>Всего приглашений</CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{referralStats?.totalReferrals || 0}</span>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription>Активных рефереров</CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{referralStats?.usersWithReferrals || 0}</span>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardDescription>Пришли по ссылке</CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{referralStats?.referredUsers || 0}</span>
            </CardContent>
          </Card>
        </div>

        {/* Top Referrers Table */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Топ рефереров
            </CardTitle>
            <CardDescription>Пользователи с наибольшим числом приглашений</CardDescription>
          </CardHeader>
          <CardContent>
            {referrersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !topReferrers?.length ? (
              <p className="text-muted-foreground text-center py-8">
                Пока нет рефералов
              </p>
            ) : (
              <div className="space-y-2">
                {topReferrers.map((user, index) => (
                  <div 
                    key={user.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                        index === 1 ? 'bg-gray-400/20 text-gray-400' :
                        index === 2 ? 'bg-amber-600/20 text-amber-600' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">
                          {user.username ? `@${user.username}` : user.first_name || 'Пользователь'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          ID: {user.telegram_id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="bg-primary/10 text-primary">
                        <UserPlus className="w-3 h-3 mr-1" />
                        {user.referral_count} приглашений
                      </Badge>
                      <Badge variant="outline">
                        <Gift className="w-3 h-3 mr-1" />
                        +{user.referral_count} хитов
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
